import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import AmpCheckRequiredItemsEditor, { countItems, EMPTY_ITEMS } from './AmpCheckRequiredItemsEditor';

const emptyCheck = (sequenceNumber) => ({
  sequenceNumber,
  sequenceLabel: '',
  description: '',
  taskReferenceId: '',
  requiredSpares: [],
  requiredConsumables: [],
  requiredToolsEquipment: []
});

const formatItemCounts = (check) => {
  const spares = check.requiredSpares?.length || 0;
  const consumables = check.requiredConsumables?.length || 0;
  const tools = check.requiredToolsEquipment?.length || 0;
  if (!spares && !consumables && !tools) return '—';
  return `${spares}S / ${consumables}C / ${tools}T`;
};

/**
 * Component for managing AMP checks within an AMP schedule or WO create checklist
 */
const AMPChecksManager = ({
  checks,
  onChange,
  title = 'AMP Checks',
  emptyMessage = 'No AMP checks added. Click "Add Check" to add one.',
  addLabel = 'Add Check'
}) => {
  const [localChecks, setLocalChecks] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editFormData, setEditFormData] = useState(emptyCheck(1));

  useEffect(() => {
    if (checks && checks.length > 0) {
      setLocalChecks(checks);
    } else {
      setLocalChecks([]);
    }
  }, [checks]);

  const handleAdd = () => {
    const newCheck = emptyCheck(localChecks.length + 1);
    const updated = [...localChecks, newCheck];
    setLocalChecks(updated);
    onChange(updated);
    setEditingIndex(updated.length - 1);
    setEditFormData(newCheck);
    setEditDialogOpen(true);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    const check = localChecks[index];
    setEditFormData({
      sequenceLabel: check.sequenceLabel ?? '',
      description: check.description ?? '',
      taskReferenceId: check.taskReferenceId ?? '',
      requiredSpares: check.requiredSpares || [],
      requiredConsumables: check.requiredConsumables || [],
      requiredToolsEquipment: check.requiredToolsEquipment || []
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editFormData.description.trim()) {
      return;
    }
    const updated = [...localChecks];
    const label = (editFormData.sequenceLabel || '').trim();
    updated[editingIndex] = {
      ...updated[editingIndex],
      sequenceLabel: label || undefined,
      description: editFormData.description,
      taskReferenceId: editFormData.taskReferenceId || '',
      requiredSpares: editFormData.requiredSpares || [],
      requiredConsumables: editFormData.requiredConsumables || [],
      requiredToolsEquipment: editFormData.requiredToolsEquipment || []
    };
    setLocalChecks(updated);
    onChange(updated);
    setEditDialogOpen(false);
    setEditingIndex(null);
    setEditFormData(emptyCheck(1));
  };

  const handleDelete = (index) => {
    const updated = localChecks.filter((_, i) => i !== index);
    const reordered = updated.map((check, i) => ({
      ...check,
      sequenceNumber: i + 1
    }));
    setLocalChecks(reordered);
    onChange(reordered);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const updated = [...localChecks];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    const reordered = updated.map((check, i) => ({
      ...check,
      sequenceNumber: i + 1
    }));
    setLocalChecks(reordered);
    onChange(reordered);
  };

  const handleMoveDown = (index) => {
    if (index === localChecks.length - 1) return;
    const updated = [...localChecks];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    const reordered = updated.map((check, i) => ({
      ...check,
      sequenceNumber: i + 1
    }));
    setLocalChecks(reordered);
    onChange(reordered);
  };

  const checkLabelForEditor = editingIndex !== null
    ? String(editFormData.sequenceLabel || localChecks[editingIndex]?.sequenceNumber || editingIndex + 1)
    : 'check';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={handleAdd}
        >
          {addLabel}
        </Button>
      </Box>

      {localChecks.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="80">S/N</TableCell>
                <TableCell width="100">S/N Label</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Task Reference ID</TableCell>
                <TableCell width="120">Required Items</TableCell>
                <TableCell width="150">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {localChecks.map((check, index) => (
                <TableRow key={index}>
                  <TableCell>{check.sequenceNumber}</TableCell>
                  <TableCell>{check.sequenceLabel && String(check.sequenceLabel).trim() ? check.sequenceLabel : '—'}</TableCell>
                  <TableCell>{check.description || '(No description)'}</TableCell>
                  <TableCell>{check.taskReferenceId || '-'}</TableCell>
                  <TableCell>
                    {countItems(check) > 0 ? (
                      <Chip size="small" label={formatItemCounts(check)} variant="outlined" />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === localChecks.length - 1}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(index)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(index)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingIndex !== null ? 'Edit AMP Check' : 'Add AMP Check'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Sequence (S/N) Label"
              value={editFormData.sequenceLabel ?? ''}
              onChange={(e) => setEditFormData({ ...editFormData, sequenceLabel: e.target.value })}
              placeholder="e.g. 1-1, 1-a (optional — falls back to row number)"
            />
            <TextField
              fullWidth
              label="Description *"
              value={editFormData.description}
              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              required
              multiline
              rows={3}
            />
            <TextField
              fullWidth
              label="Task Reference ID"
              value={editFormData.taskReferenceId}
              onChange={(e) => setEditFormData({ ...editFormData, taskReferenceId: e.target.value })}
              placeholder="Optional"
            />
            <AmpCheckRequiredItemsEditor
              requiredSpares={editFormData.requiredSpares || EMPTY_ITEMS.requiredSpares}
              requiredConsumables={editFormData.requiredConsumables || EMPTY_ITEMS.requiredConsumables}
              requiredToolsEquipment={editFormData.requiredToolsEquipment || EMPTY_ITEMS.requiredToolsEquipment}
              checkLabel={checkLabelForEditor}
              onChange={(items) => setEditFormData({ ...editFormData, ...items })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={!editFormData.description.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AMPChecksManager;
